import {cn, InlineSvg, RootLayout, RsnButton, styles, useBoolean} from '@raison/ui-kit';
import {CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe} from '@stripe/react-stripe-js';
import type {StripeCardNumberElementChangeEvent, StripeElementChangeEvent, StripeElementType} from '@stripe/stripe-js';
import {StripeElementStyle} from '@stripe/stripe-js/dist/stripe-js/elements/base';
import {useQueryClient} from '@tanstack/react-query';
import React, {useState} from 'react';
import {useNavigate} from 'react-router';
import {GenericErrorMsg} from '~/entities/dialogs';
import {cardsQueryKey} from '~/processes/cards/api/cardQueryHooks';
import {useAddNewCard} from '~/processes/cards/api/newCard';
import {CardBrandIcon, CardBrandName} from '~/processes/cards/ui/add/CardBrandIcon';
import {ElementFieldLayout} from '~/processes/cards/ui/add/ElementFieldLayout';
import StripeWrapper from '~/processes/fiat-operations/ui/Shared/StripeWrapper';
import {useAppDispatch} from '~/shared/store';
import checkIcon from '../../../../../../static/icons/check-line.svg';
import StripeLogo from './stripe.svg';

type Fields = Extract<StripeElementType, 'cardNumber' | 'cardExpiry' | 'cardCvc'> | 'cardHolderName';
type Form = {
    cardHolderName: string;
    cardBrandName: CardBrandName;
    completed: Record<Fields, boolean>;
    errors: Record<Fields, string | undefined>;
};
const Form: Form = {
    cardHolderName: '',
    cardBrandName: 'unknown',
    completed: {
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false,
        cardHolderName: false,
    },
    errors: {
        cardNumber: undefined,
        cardExpiry: undefined,
        cardCvc: undefined,
        cardHolderName: undefined,
    },
};

export interface AddNewCardPageProps {
    phone: string;
    email: string;
}

export function AddNewCardPage(props: AddNewCardPageProps) {
    const [screen, setScreen] = useState<'form' | 'success'>('form');

    if (screen === 'success') {
        return <SuccessfullyAdded />;
    }

    return (
        <RootLayout title='New card'>
            <StripeWrapper>
                <AddNewCardForm {...props} onSuccess={() => setScreen('success')} />
            </StripeWrapper>
        </RootLayout>
    );
}

export interface AddNewCardFormProps extends AddNewCardPageProps {
    onSuccess: () => void;
}

export function AddNewCardForm({email, phone, onSuccess}: AddNewCardFormProps) {
    const [form, setForm] = useState<Form>(() => Form);

    const handleElementChange = (e: StripeElementChangeEvent) => {
        setForm(prev => ({
            ...prev,
            cardBrandName:
                e.elementType === 'cardNumber' ? (e as StripeCardNumberElementChangeEvent).brand : prev.cardBrandName,
            completed: {...prev.completed, [e.elementType]: e.complete},
            errors: {...prev.errors, [e.elementType]: e.error?.message},
        }));
    };
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const error = validateCardHolderName(form.cardHolderName);
        setForm(prev => ({
            ...prev,
            cardHolderName: e.target.value.toUpperCase(),
            completed: {...prev.completed, cardHolderName: e.target.value.length > 0 && !error},
            errors: {...prev.errors, cardHolderName: undefined},
        }));
    };

    const handleNameBlur = () => {
        const error = validateCardHolderName(form.cardHolderName);
        setForm(prev => ({
            ...prev,
            errors: {...prev.errors, cardHolderName: error},
            completed: {...prev.completed, cardHolderName: form.cardHolderName.length > 0 && !error},
        }));
    };

    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();

    const {mutateAsync: getClientSecret} = useAddNewCard();

    async function confirmCardSetup(secret: string) {
        if (!stripe || !elements) {
            throw new Error('Stripe or Elements not initialized');
        }

        const card = elements.getElement(CardNumberElement);
        if (!card) {
            throw new Error('Card element not found');
        }

        const result = await stripe.confirmCardSetup(secret, {
            payment_method: {
                card,
                billing_details: {
                    name: form.cardHolderName.replace(/\s{1,}/g, ' ').trim(),
                    email,
                    phone,
                },
            },
        });

        console.log(result.error);

        if (!result.error) {
            onSuccess();
            queryClient.invalidateQueries({queryKey: cardsQueryKey});
            return;
        }

        const errorTitle = result.error.type === 'api_connection_error' ? 'Connection issue' : 'Something went wrong';

        dispatch(
            GenericErrorMsg.open({
                title: errorTitle,
                details: result.error.message,
            })
        );
    }

    async function submitCard() {
        const {client_secret} = await getClientSecret().catch(e => {
            dispatch(GenericErrorMsg.open({details: 'We couldn’t add card now. Please try again later.'}));
            throw e;
        });
        await confirmCardSetup(client_secret);
    }

    const [isLoading, loading] = useBoolean(false);

    const isComplete = Object.values(form.completed).every(el => el);
    const canSubmit = !isLoading && isComplete;

    return (
        <form
            className={'flex flex-1 flex-col'}
            onSubmit={e => {
                e.preventDefault();
                loading.on();
                submitCard().finally(loading.off);
            }}
        >
            <div className={'flex-1 space-y-6 p-4'}>
                <ElementFieldLayout
                    label={'Card number'}
                    decoration={<CardBrandIcon brandName={form.cardBrandName} />}
                    issue={form.errors.cardNumber}
                >
                    <CardNumberElement
                        options={{style: elementStyle}}
                        className={'~Field__input'}
                        onChange={handleElementChange}
                    />
                </ElementFieldLayout>
                <fieldset className={'relative'}>
                    <div className={'grid grid-cols-2 gap-2'}>
                        <ElementFieldLayout label={'Valid thru'} isInvalid={!!form.errors.cardExpiry}>
                            <CardExpiryElement
                                options={{style: elementStyle}}
                                className={'~Field__input'}
                                onChange={handleElementChange}
                            />
                        </ElementFieldLayout>
                        <ElementFieldLayout label={'CVC code'} isInvalid={!!form.errors.cardCvc}>
                            <CardCvcElement
                                options={{style: elementStyle}}
                                className={'~Field__input'}
                                onChange={handleElementChange}
                            />
                        </ElementFieldLayout>
                    </div>
                    <span
                        className={
                            'fv-text-m-regular absolute left-0 top-full pt-1 text-danger-30 group-[.bad]:text-danger-30'
                        }
                    >
                        {form.errors.cardExpiry || form.errors.cardCvc}
                    </span>
                </fieldset>
                <ElementFieldLayout label={'Name on card'} issue={form.errors.cardHolderName}>
                    <input
                        inputMode={'text'}
                        type={'text'}
                        name={'cardholder'}
                        placeholder={'John Doe'}
                        autoComplete={'cc-name'}
                        className={'~Field__input focus:border-neutral-40 focus:shadow-none'}
                        value={form.cardHolderName}
                        onChange={handleNameChange}
                        onBlur={handleNameBlur}
                    />
                </ElementFieldLayout>
            </div>

            <div className={'flex flex-col gap-4 p-4'}>
                <div className={'flex items-center justify-center gap-4'}>
                    <a
                        className={cn(cls.link, 'inline-flex items-center gap-0.5')}
                        href={'https://stripe.com/?ios=1&key=4sf4tNk97JnZlpZ6'}
                        target={'_blank'}
                        rel='noreferrer'
                    >
                        Powered by <InlineSvg src={StripeLogo} className={'h-4'} />
                    </a>
                    <hr className={'h-full border-b-0 border-r border-solid border-neutral-40'} />
                    <a
                        className={cls.link}
                        href={'https://stripe.com/payment-terms/legal?ios=1&key=4sf4tNk97JnZlpZ6'}
                        target={'_blank'}
                        rel='noreferrer'
                    >
                        Terms
                    </a>
                    <a
                        className={cls.link}
                        href={'https://stripe.com/privacy?ios=1&key=4sf4tNk97JnZlpZ6'}
                        target={'_blank'}
                        rel='noreferrer'
                    >
                        Privacy
                    </a>
                </div>
                <RsnButton.Primary disabled={!canSubmit} type={'submit'}>
                    Save card
                </RsnButton.Primary>
            </div>
        </form>
    );
}

const cls = styles({
    link: 'fv-text-m-regular text-[#9DA0A6]',
});

const onlyLettersAndSpaceRegex = new RegExp(/^[a-zA-Z0-9 ]*$/);
const validateCardHolderName = (name: string): string => {
    if (name.length > 26) return 'The maximum name length is limited to 26 characters';
    if (!onlyLettersAndSpaceRegex.test(name)) return 'Only numbers and Latin letters are allowed';
    if (name.length > 0 && !name.trim().length) return 'The minimum name length is 1 character';
    return '';
};

const elementStyle: StripeElementStyle = {
    base: {
        'fontSize': '16px',
        'lineHeight': '42px',
        'color': '#0A0A0A',
        'fontFamily': 'DMSans Regular, sans-serif',
        'fontWeight': 400,
        ':focus': {},
    },
    empty: {
        '::placeholder': {
            color: '#9E9E9E',
            fontWeight: 400,
        },
    },
    invalid: {
        color: '#0A0A0A',
    },
};

function SuccessfullyAdded() {
    const navigate = useNavigate();
    const handleClick = () => navigate('/cards');
    return (
        <div className='flex h-full flex-col'>
            <div className='flex flex-1 flex-col items-center justify-center gap-6 p-4'>
                <div className='rounded-full bg-success-10 p-[18px] text-[0]'>
                    <InlineSvg src={checkIcon} className='h-[60px] w-[60px] text-success-30' />
                </div>
                <div className='flex flex-col gap-2'>
                    <h2 className='fv-heading-l text-center text-neutral-100'>Card added</h2>
                    <span className='fv-text-l-regular max-w-[326px] text-center text-neutral-90'>
                        This card can now be selected as a payment method.
                    </span>
                </div>
            </div>

            <div className='flex flex-col gap-2 px-4 pb-8'>
                <RsnButton.Primary onClick={handleClick}>Back to cards</RsnButton.Primary>
            </div>
        </div>
    );
}
